import * as THREE from 'three';

/**
 * Flat track name painted on the map (canvas texture on a horizontal plane).
 */
export function createTrackNameLabel() {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.premultiplyAlpha = false;
  texture.generateMipmaps = false;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;

  const material = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    depthWrite: false,
    depthTest: false,
    side: THREE.DoubleSide,
    fog: false,
  });

  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), material);
  mesh.rotation.x = -Math.PI / 2;
  mesh.renderOrder = 8;

  return { mesh, canvas, ctx, texture };
}

/**
 * @param {{ mesh: THREE.Mesh; canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D; texture: THREE.CanvasTexture }} state
 * @param {{
 *   showTrackName?: boolean;
 *   trackNameText?: string;
 *   trackNameFontSize?: number;
 *   trackNameColor?: string;
 *   trackNameOpacity?: number;
 *   trackNamePosX?: number;
 *   trackNamePosZ?: number;
 *   trackNameRotation?: number;
 *   trackLift?: number;
 *   mapOffsetX?: number;
 *   mapOffsetZ?: number;
 *   mapSize?: number;
 * }} settings
 */

/** Max ground footprint for the title as a fraction of map size (single-line fit). */
const TRACK_NAME_MAX_MAP_FRACTION = 0.64;

/**
 * @param {string} raw
 */
function normalizeTrackNameText(raw) {
  return raw.trim().replace(/\s*\n+\s*/g, ' ').replace(/\s+/g, ' ').toUpperCase();
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} text
 * @param {string} font
 */
function measureTrackNameCanvas(text, ctx, font) {
  ctx.font = font;
  const metrics = ctx.measureText(text);
  const left = metrics.actualBoundingBoxLeft ?? 0;
  const right = metrics.actualBoundingBoxRight ?? metrics.width;
  const ascent = metrics.actualBoundingBoxAscent ?? 0;
  const descent = metrics.actualBoundingBoxDescent ?? 0;
  const textWidth = left + right;
  const textHeight = ascent + descent;
  return { textWidth, textHeight, left, ascent };
}

/**
 * @param {number} worldW
 * @param {number} worldH
 * @param {number} rotationDeg
 */
function trackNameGroundFootprint(worldW, worldH, rotationDeg) {
  const rad = THREE.MathUtils.degToRad(rotationDeg);
  const c = Math.abs(Math.cos(rad));
  const s = Math.abs(Math.sin(rad));
  return Math.max(worldW * c + worldH * s, worldW * s + worldH * c);
}

export async function updateTrackNameLabel(state, settings) {
  const { mesh, canvas, ctx, texture } = state;
  const {
    showTrackName = true,
    trackNameText = '',
    trackNameFontSize = 80,
    trackNameColor = '#d8d8d8',
    trackNameOpacity = 0.4,
    trackNamePosX = 0,
    trackNamePosZ = 0,
    trackNameRotation = -32,
    trackLift = 0,
    mapOffsetX = 0,
    mapOffsetZ = 0,
    mapSize = 16,
  } = settings;

  const text = normalizeTrackNameText(trackNameText);
  mesh.visible = showTrackName && text.length > 0;

  if (!mesh.visible) return;

  try {
    await document.fonts.load(`800 ${trackNameFontSize}px Inter`);
  } catch {
    /* Inter may already be loaded */
  }

  const font = `800 ${trackNameFontSize}px Inter, system-ui, sans-serif`;
  const { textWidth, textHeight, left, ascent } = measureTrackNameCanvas(text, ctx, font);
  const padX = trackNameFontSize * 0.35;
  const padY = trackNameFontSize * 0.2;
  const w = Math.max(64, Math.ceil(textWidth + padX * 2));
  const h = Math.max(32, Math.ceil(textHeight + padY * 2));

  canvas.width = w;
  canvas.height = h;

  ctx.font = font;
  ctx.fillStyle = trackNameColor;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.clearRect(0, 0, w, h);
  ctx.fillText(text, padX + left, padY + ascent);
  texture.needsUpdate = true;

  const worldScale = trackNameFontSize * 0.00034;
  const worldW = w * worldScale;
  const worldH = h * worldScale;
  const maxFootprint = mapSize * TRACK_NAME_MAX_MAP_FRACTION;
  const footprint = trackNameGroundFootprint(worldW, worldH, trackNameRotation);
  const fitScale = footprint > maxFootprint ? maxFootprint / footprint : 1;
  mesh.scale.set(worldW * fitScale, worldH * fitScale, 1);

  const groundY = trackLift > 0.05 ? 0.08 : 0.09;
  mesh.position.set(
    trackNamePosX + mapOffsetX,
    groundY,
    trackNamePosZ + mapOffsetZ,
  );
  mesh.rotation.x = -Math.PI / 2;
  mesh.rotation.z = THREE.MathUtils.degToRad(trackNameRotation);
  mesh.material.opacity = trackNameOpacity;
}

export function disposeTrackNameLabel(state) {
  state.texture.dispose();
  state.mesh.geometry.dispose();
  state.mesh.material.dispose();
}
